import { IUser } from "../../auth/authentication/models/auth-response";
import { IAccountMailbox } from "./account.models";
import { IConversationTimelineObject } from "./timeline.models";

/** Minimal customer (location) info for conversation association display */

export interface IConversationCustomerOrder {
  id: number;
  name: string;
  date: string;
  total_amount: number;
  status: string;
}

export interface IConversationThreadContext {
  company_name: string;
  company_currency?: {
    id: number;
    name: string;
    symbol: string;
    position: "after" | "before";
  };
  sender: {
    email: string;
    name: string;
    is_contact: boolean;
  };
  kpis: {
    total_orders_amount: number;
    average_order_value: number;
    average_sku_count_per_order: number;
  };
  last_orders: IConversationCustomerOrder[];
  products_kpis: {
    favorite_brand: string | null;
    products_bought_count: number;
  };
}

/** Product item returned by thread customer products API (sidebar listings). */
export interface IConversationCustomerProduct {
  product_tmpl_id: number;
  product_id: number;
  pricelist_id: number;
  name: string;
  brand: string | false;
  sku: string | false;
  barcode: string | false;
  sales_price: number;
  available_quantity: number;
}

/** Catalog item returned by thread customer catalogs API (sidebar listings). */
export interface IConversationCustomerCatalog {
  id: number;
  name: string;
  image: string | null;
  nbr_products: number;
}

export interface IConversationCustomer {
    id: number;
    company_name: string;
    location: string;
}

export interface IConversation {
    id: number;
    subject: string;
    sender: string;
    sender_email: string;
    mailbox: IAccountMailbox;
    provider_thread_id: string;
    last_message_at: string;
    message_count: number;
    attachment_count: number;
    is_read: boolean;
    assigned_to_id: string;
    urgency: string;
    /**
     * Effective thread urgency: true when any message is AI-urgent, unless an
     * operator override says otherwise. The override wins, so a thread can be
     * `false` while still containing an `is_urgent` message — that is expected.
     */
    is_urgent?: boolean;
    /** Category of the most recent classified message. */
    classification: ConversationClassification;
    /** All distinct categories across the thread's messages, most recent first. */
    categories?: ConversationClassification[];
    status: ConversationStatus;
    assignee: IUser | null;

    customer: IConversationCustomer | null;
}

export interface IConversationDetails extends IConversation {
    thread_context?: IConversationThreadContext | null;
    /** Ordered timeline of messages and events (extensible for mentions, comments, replies). */
    objects?: IConversationTimelineObject[];
    /**
     * Server-derived: a create-order job is still running for this thread (claim
     * set and within TTL). The client re-arms the in-progress skeleton from this
     * on load, so it survives a refresh instead of living only in the store.
     */
    order_creation_in_progress?: boolean;
}

/** A single sent (outbound) message shown in the Sent folder (flat list of messages, not threads). */
export interface ISentMessage {
    id: number;            // Odoo message id (used to highlight the message in the timeline)
    thread_id: number;     // Odoo thread id (used to open the conversation)
    subject: string;
    recipients: string;    // "To" addresses, comma-separated
    sender: string;
    sender_email: string;
    sender_name: string;
    timestamp: string;
    is_read: boolean;
    has_attachments: boolean;
    attachment_count: number;
    mailbox: IAccountMailbox;
}

/** Params for getSentMessages API: filters + pagination */
export interface IGetSentMessagesParams {
    mailbox_ids: number[];
    search_term?: string;
    date_from?: string;
    date_to?: string;
    has_attachment?: boolean;
    page?: number;
    limit?: number;
}

export interface IConversationUpdate {
    is_read?: boolean;
    category?: ConversationClassification;
    status?: ConversationStatus;
    assignee_id?: number | null; // Platform User ID
    customer_id?: number | null; // Company Location
}

/** Payload emitted after a successful bulk update so the parent can patch the conversation list */
export interface IConversationUpdateEvent {
    conversationIds: number[];
    payload: IConversationUpdate;
}

/** Reusable filters for listing conversations (mailboxes, categories, statuses, assignees, search key, advanced) */
export interface IConversationListFilters {
    mailbox_ids: number[];
    categories?: ConversationClassification[];
    statuses?: ConversationStatus[];
    assignee_ids?: number[];
    /**
     * Team ids: narrows to threads assigned to any member of ANY of these teams.
     * Omitted when unset -- never sent as an empty list.
     */
    team_ids?: number[];
    search_term?: string;
    from?: string;
    to?: string;
    subject?: string;
    includes_words?: string;
    excludes_words?: string;
    date_from?: string;
    date_to?: string;
    has_attachment?: boolean;
    customer_ids?: number[];
    /** Filter on the thread's effective (override-aware) urgency. */
    is_urgent?: boolean;
}

/** Body of PUT /inbox/messages/{id}/classification — at least one field required. */
export interface IMessageClassificationUpdate {
    category?: ConversationClassification;
    /** Uppercase intent key, or null to clear it. */
    intent?: MessageIntent | null;
}

/** Object returned by the message-classification override endpoint. */
export interface IMessageClassificationResult {
    id: number;
    classification: ConversationClassification;
    classification_confidence?: number;
    intent: MessageIntent | null;
    intent_confidence?: number;
    is_urgent?: boolean;
}

/** Object returned by the thread-urgency override endpoint. */
export interface IThreadUrgencyResult {
    id: number;
    is_urgent: boolean;
}

/** Params for getConversations API: filters + pagination */
export interface IGetConversationsParams extends IConversationListFilters {
    page?: number;
    limit?: number;
}

export const ConversationClassification = {
    ORDER: 'order',
    RFQ: 'rfq',
    SUPPORT: 'support',
    INQUIRY: 'inquiry',
    ADMIN: 'admin',
    SPAM: 'spam',
    OTHER: 'other',
    UNDEFINED: 'undefined'
} as const;

export type ConversationClassification = (typeof ConversationClassification)[keyof typeof ConversationClassification];

/**
 * Fine-grained intent detected by the classifier. The backend treats this as an
 * open uppercase string (`^[A-Z][A-Z_]{0,79}$`) it can extend without a release,
 * so the type stays open. Operators pick from MESSAGE_INTENTS_BY_CATEGORY only;
 * any value the AI invents still renders via `intentLabelKey()`'s fallback.
 */
export type MessageIntent = string;

/**
 * Intents offered per category — the classifier's actual vocabulary. The picker
 * only lists intents valid for the message's current category. Categories absent
 * from this map (`spam`, `other`) are not operator-selectable; they still render
 * when the backend sends them.
 */
export const MESSAGE_INTENTS_BY_CATEGORY: Partial<Record<ConversationClassification, MessageIntent[]>> = {
    [ConversationClassification.ORDER]: [
        'ORDER_PLACING',
        'ORDER_UPDATE',
        'ORDER_CANCELLATION',
        'OTHER',
    ],
    [ConversationClassification.RFQ]: [
        'REQUEST_FOR_QUOTATION',
        'QUOTE_ACCEPTANCE',
        'QUOTE_REJECTION',
        'QUOTE_UPDATE',
        'OTHER',
    ],
    [ConversationClassification.SUPPORT]: [
        'DELIVERY_REQUEST',
        'RETURN_REQUEST',
        'PRODUCT_CLAIM',
        'OTHER',
    ],
    [ConversationClassification.INQUIRY]: [
        'SPECS_REQUEST',
        'DOCUMENTATION_REQUEST',
        'SUBSTITUTE_REQUEST',
        'UPGRADE_REQUEST',
        'DOWNGRADE_REQUEST',
        'OTHER',
    ],
    [ConversationClassification.ADMIN]: [
        'INVOICING',
        'PAYMENT_TERMS',
        'OTHER',
    ],
    [ConversationClassification.UNDEFINED]: [
        'OTHER',
    ],
};

/**
 * Categories an operator can assign. Derived from the intent map so the two can
 * never drift — a category without a defined intent list is not offered.
 */
export const SELECTABLE_CLASSIFICATIONS = Object.keys(MESSAGE_INTENTS_BY_CATEGORY) as ConversationClassification[];

export const ConversationStatus = {
    NEW: 'new',
    OPEN: 'open',
    ON_HOLD: 'on_hold',
    CLOSED: 'closed',
} as const;
export type ConversationStatus = (typeof ConversationStatus)[keyof typeof ConversationStatus];

export const ConversationMenuOptions = {
    CATEGORY: 'category',
    STATUS: 'status',
    ASSIGN: 'assign',
    MORE: 'more'
} as const;

export type ConversationMenuOptions = (typeof ConversationMenuOptions)[keyof typeof ConversationMenuOptions];
/** 202 body of `POST /threads/{id}/create-order` — an acknowledgement, never the order. */
export interface ICreateOrderAck {
    thread_id: number;
    status: string;
}
